/**
 * Terms and Conditions page - publicly accessible legal document.
 * Required for Azure Communication Services regulatory compliance.
 */

import { makeStyles, tokens, Text } from '@fluentui/react-components';

const useStyles = makeStyles({
    wrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        fontFamily: tokens.fontFamilyBase,
        lineHeight: '1.6',
        color: tokens.colorNeutralForeground1,
    },
    title: {
        fontSize: tokens.fontSizeHero800,
        fontWeight: tokens.fontWeightBold,
        marginBottom: '8px',
    },
    lastUpdated: {
        color: tokens.colorNeutralForeground3,
        marginBottom: '32px',
    },
    section: {
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '12px',
        marginTop: '24px',
    },
    paragraph: {
        marginBottom: '12px',
    },
    list: {
        marginLeft: '24px',
        marginBottom: '12px',
    },
    listItem: {
        marginBottom: '8px',
    },
    disclaimer: {
        textTransform: 'uppercase',
        fontWeight: tokens.fontWeightSemibold,
    },
    contact: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
});

export function TermsPage(): JSX.Element {
    const styles = useStyles();

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <h1 className={styles.title}>Terms and Conditions</h1>
            <Text className={styles.lastUpdated}>Last updated: December 2, 2024</Text>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
                <p className={styles.paragraph}>
                    By accessing and using IRPilot (the &quot;Service&quot;), provided by MacGregor Consulting LLC
                    (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), you accept and agree to be bound by these Terms and Conditions.
                    If you do not agree to these terms, please do not use this Service.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>2. Description of Service</h2>
                <p className={styles.paragraph}>
                    IRPilot is an IT administration platform for managing Office 365 tenants, including
                    user provisioning, license management, and secure credential delivery via SMS. The
                    Service is intended for use by authorized IT administrators and business users only.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>3. Use License</h2>
                <p className={styles.paragraph}>
                    We grant you a personal, non-exclusive, non-transferable license to access and use
                    the Service for your internal business purposes. You may not:
                </p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>Copy, modify, or distribute the Service or its content</li>
                    <li className={styles.listItem}>Reverse engineer or attempt to extract the source code</li>
                    <li className={styles.listItem}>Use the Service for any unlawful purpose</li>
                    <li className={styles.listItem}>Resell or sublicense access to the Service</li>
                </ul>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>4. User Accounts and Security</h2>
                <p className={styles.paragraph}>You are responsible for:</p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>Maintaining the confidentiality of your account credentials</li>
                    <li className={styles.listItem}>All activities that occur under your account</li>
                    <li className={styles.listItem}>Notifying us immediately of any unauthorized access</li>
                </ul>
                <p className={styles.paragraph}>
                    You agree not to share your account credentials or allow unauthorized persons to access
                    the Service through your account.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>5. SMS Messaging Terms</h2>
                <p className={styles.paragraph}>By using the SMS credential delivery feature:</p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>
                        You confirm you have authorization to send SMS messages to the phone numbers you enter
                    </li>
                    <li className={styles.listItem}>
                        You agree to use SMS only for legitimate business purposes (security credential delivery)
                    </li>
                    <li className={styles.listItem}>
                        You acknowledge that standard message and data rates may apply to recipients
                    </li>
                    <li className={styles.listItem}>
                        You agree not to use the Service for spam, marketing, or unsolicited messages
                    </li>
                </ul>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>6. Privacy Policy</h2>
                <p className={styles.paragraph}>
                    Your use of the Service is also governed by our Privacy Policy, which describes how
                    we collect, use, and protect your information. Please review our Privacy Policy for details.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>7. Third-Party Services</h2>
                <p className={styles.paragraph}>
                    The Service integrates with Microsoft Azure and Office 365. Your use of these services
                    is subject to Microsoft&apos;s terms of service and privacy policies. We are not responsible
                    for the availability or functionality of third-party services.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>8. Security</h2>
                <p className={styles.paragraph}>You are prohibited from:</p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>
                        Attempting to gain unauthorized access to any part of the Service
                    </li>
                    <li className={styles.listItem}>
                        Probing, scanning, or testing the vulnerability of the Service
                    </li>
                    <li className={styles.listItem}>Interfering with or disrupting the Service</li>
                    <li className={styles.listItem}>Uploading malicious code or content</li>
                </ul>
                <p className={styles.paragraph}>
                    Violations may result in termination of access and potential legal action.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>9. Disclaimer of Warranties</h2>
                <p className={`${styles.paragraph} ${styles.disclaimer}`}>
                    THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS. MACGREGOR CONSULTING LLC
                    MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
                    OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className={styles.paragraph}>We do not warrant that:</p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>The Service will be uninterrupted or error-free</li>
                    <li className={styles.listItem}>Defects will be corrected</li>
                    <li className={styles.listItem}>The Service is free of viruses or harmful components</li>
                </ul>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>10. Limitation of Liability</h2>
                <p className={`${styles.paragraph} ${styles.disclaimer}`}>
                    TO THE FULLEST EXTENT PERMITTED BY LAW, MACGREGOR CONSULTING LLC SHALL NOT BE LIABLE
                    FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                    BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR
                    USE OF THE SERVICE.
                </p>
                <p className={`${styles.paragraph} ${styles.disclaimer}`}>
                    IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12)
                    MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>11. Indemnification</h2>
                <p className={styles.paragraph}>
                    You agree to indemnify and hold harmless MacGregor Consulting LLC and its officers,
                    directors, employees, and agents from any claims, damages, losses, or expenses
                    (including reasonable attorneys&apos; fees) arising from your use of the Service or
                    violation of these Terms.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>12. Service Availability</h2>
                <p className={styles.paragraph}>
                    We reserve the right to modify, suspend, or discontinue any part of the Service at
                    any time without notice. We shall not be liable for any modification, suspension, or
                    discontinuance of the Service.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>13. Modifications to Terms</h2>
                <p className={styles.paragraph}>
                    We may revise these Terms at any time without notice. By continuing to use the Service
                    after changes are posted, you agree to be bound by the revised Terms. We encourage you
                    to review these Terms periodically.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>14. Governing Law and Dispute Resolution</h2>
                <p className={styles.paragraph}>
                    These Terms shall be governed by and construed in accordance with the laws of the
                    State of Tennessee, without regard to its conflict of law provisions.
                </p>
                <p className={styles.paragraph}>
                    Any dispute arising from these Terms or your use of the Service shall be resolved
                    through binding arbitration in accordance with the rules of the American Arbitration
                    Association. The arbitration shall take place in Tennessee, and the arbitrator&apos;s
                    decision shall be final and binding.
                </p>
                <p className={styles.paragraph}>
                    You agree to waive any right to a jury trial and to participate in a class action
                    lawsuit or class-wide arbitration.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>15. Severability</h2>
                <p className={styles.paragraph}>
                    If any provision of these Terms is found to be unenforceable, the remaining provisions
                    shall continue in full force and effect.
                </p>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>16. Entire Agreement</h2>
                <p className={styles.paragraph}>
                    These Terms, together with our Privacy Policy, constitute the entire agreement between
                    you and MacGregor Consulting LLC regarding the Service and supersede any prior agreements.
                </p>
            </div>

            <div className={styles.contact}>
                <h2 className={styles.sectionTitle}>17. Contact Information</h2>
                <p className={styles.paragraph}>
                    If you have any questions about these Terms and Conditions, please contact us at:
                </p>
                <p className={styles.paragraph}>
                    <strong>MacGregor Consulting LLC</strong><br />
                    Email: connect@macgregor.consulting
                </p>
            </div>
            </div>
        </div>
    );
}
